import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  orderBy,
  limit,
  query,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { LocationPrefs, MemberLocation, Geofence, GeofenceEvent, Checkin } from '@/types';

interface LocationContextType {
  locationPrefs: LocationPrefs | null;
  memberLocations: Map<string, MemberLocation>;
  geofences: Geofence[];
  geofenceEvents: GeofenceEvent[];
  checkins: Checkin[];
  updateSharing: (enabled: boolean) => Promise<void>;
  pauseSharing: (hours: number) => Promise<void>;
  updateLocation: (position: GeolocationPosition) => Promise<void>;
  submitCheckin: (zoneName: string) => Promise<void>;
  addGeofence: (fence: Omit<Geofence, 'id' | 'createdBy' | 'createdAt'>) => Promise<void>;
  deleteGeofence: (fenceId: string) => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: React.ReactNode;
  userId: string | undefined;
  householdId: string | null | undefined;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
  userId,
  householdId,
}) => {
  const [locationPrefs, setLocationPrefs] = useState<LocationPrefs | null>(null);
  const [memberLocations, setMemberLocations] = useState<Map<string, MemberLocation>>(new Map());
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [geofenceEvents, setGeofenceEvents] = useState<GeofenceEvent[]>([]);
  const [checkins, setCheckins] = useState<Checkin[]>([]);
  const batteryLevelRef = useRef<number | null>(null);
  const unsubs = useRef<Array<() => void>>([]);

  useEffect(() => {
    unsubs.current.forEach((u) => u());
    unsubs.current = [];

    if (!userId) return;

    const prefRef = doc(db, 'users', userId, 'locationPrefs', 'prefs');
    unsubs.current.push(
      onSnapshot(prefRef, (snap) => {
        setLocationPrefs(snap.exists() ? (snap.data() as LocationPrefs) : null);
      })
    );

    if (!householdId) return;

    unsubs.current.push(
      onSnapshot(collection(db, 'households', householdId, 'memberLocations'), (snap) => {
        const m = new Map<string, MemberLocation>();
        snap.docs.forEach((d) => m.set(d.id, d.data() as MemberLocation));
        setMemberLocations(m);
      })
    );

    unsubs.current.push(
      onSnapshot(collection(db, 'households', householdId, 'geofences'), (snap) => {
        setGeofences(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Geofence, 'id'>) })));
      })
    );

    const eventsQ = query(
      collection(db, 'households', householdId, 'geofenceEvents'),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    unsubs.current.push(
      onSnapshot(eventsQ, (snap) => {
        setGeofenceEvents(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<GeofenceEvent, 'id'>) }))
        );
      })
    );

    const checkinsQ = query(
      collection(db, 'households', householdId, 'checkins'),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    unsubs.current.push(
      onSnapshot(checkinsQ, (snap) => {
        setCheckins(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Checkin, 'id'>) }))
        );
      })
    );

    return () => {
      unsubs.current.forEach((u) => u());
      unsubs.current = [];
    };
  }, [userId, householdId]);

  // Battery monitoring
  useEffect(() => {
    if (!('getBattery' in navigator)) return;
    (navigator as any).getBattery().then((battery: any) => {
      batteryLevelRef.current = battery.level;
      battery.addEventListener('levelchange', () => {
        batteryLevelRef.current = battery.level;
      });
    });
  }, []);

  // Geolocation watch when sharing is active
  useEffect(() => {
    if (!userId || !householdId) return;
    if (!locationPrefs?.sharing) return;

    const isPaused =
      locationPrefs.pausedUntil !== null &&
      locationPrefs.pausedUntil.toMillis() > Date.now();
    if (isPaused) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => updateLocation(pos),
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 30000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, householdId, locationPrefs?.sharing, locationPrefs?.pausedUntil]);

  const updateSharing = async (enabled: boolean) => {
    if (!userId) return;
    const prefRef = doc(db, 'users', userId, 'locationPrefs', 'prefs');
    await setDoc(
      prefRef,
      { sharing: enabled, pausedUntil: null, lastUpdated: serverTimestamp() },
      { merge: true }
    );
  };

  const pauseSharing = async (hours: number) => {
    if (!userId) return;
    const prefRef = doc(db, 'users', userId, 'locationPrefs', 'prefs');
    const pausedUntil = Timestamp.fromMillis(Date.now() + hours * 60 * 60 * 1000);
    await setDoc(prefRef, { pausedUntil, lastUpdated: serverTimestamp() }, { merge: true });
  };

  const updateLocation = async (position: GeolocationPosition) => {
    if (!userId || !householdId) return;
    const locRef = doc(db, 'households', householdId, 'memberLocations', userId);
    await setDoc(locRef, {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      batteryLevel: batteryLevelRef.current,
      timestamp: serverTimestamp(),
      lastKnown: false,
    });
  };

  const submitCheckin = async (zoneName: string) => {
    if (!userId || !householdId) return;
    await addDoc(collection(db, 'households', householdId, 'checkins'), {
      userId,
      zoneName,
      timestamp: serverTimestamp(),
    });
  };

  const addGeofence = async (fence: Omit<Geofence, 'id' | 'createdBy' | 'createdAt'>) => {
    if (!userId || !householdId) return;
    await addDoc(collection(db, 'households', householdId, 'geofences'), {
      ...fence,
      createdBy: userId,
      createdAt: serverTimestamp(),
    });
  };

  const deleteGeofence = async (fenceId: string) => {
    if (!householdId) return;
    await deleteDoc(doc(db, 'households', householdId, 'geofences', fenceId));
  };

  return (
    <LocationContext.Provider
      value={{
        locationPrefs,
        memberLocations,
        geofences,
        geofenceEvents,
        checkins,
        updateSharing,
        pauseSharing,
        updateLocation,
        submitCheckin,
        addGeofence,
        deleteGeofence,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used within LocationProvider');
  return ctx;
};
